import i18next from 'i18next';
import { encode, decode } from 'js-base64';
import { map } from 'lodash';
import moment from 'moment';

import { FormikSelectOption } from 'components';
import { DateFormat, EnabledStatus, FilterDefinitionType } from 'enums';
import { DateFilters, FilterDefinition } from 'types';
import { formatUtils } from 'utils';

const getDataRangeFilters = <T>(
  filters: T,
  formatType?:
    | FilterDefinitionType.DateRange
    | FilterDefinitionType.DateTimeRange,
  label?: string,
  nameFrom: Extract<keyof T, string> = 'from' as Extract<keyof T, string>,
  nameTo: Extract<keyof T, string> = 'to' as Extract<keyof T, string>,
): FilterDefinition<T>[] => {
  const { t } = i18next;
  const type = formatType ?? FilterDefinitionType.DateTimeRange;
  const getDisplayName = (value: string) =>
    formatUtils.formatDate(value, { format: DateFormat.Long });

  return [
    {
      from: {
        name: nameFrom,
        label: t('filters.date_from', {
          title: label || t('filters.date'),
        }),
      },
      to: {
        name: nameTo,
        label: t('filters.date_to', { title: label || t('filters.date') }),
      },
      type,
      startOf: 'minute',
      maxDate: filters?.[nameTo]
        ? moment(filters?.[nameTo] as string)
        : undefined,
      minDate: filters?.[nameFrom]
        ? moment(filters?.[nameFrom] as string)
        : undefined,
      getDisplayName,
    },
  ];
};

const getCommonFilters = (
  filters: DateFilters,
  formatType?:
    | FilterDefinitionType.DateRange
    | FilterDefinitionType.DateTimeRange,
): FilterDefinition<DateFilters>[] => getDataRangeFilters(filters, formatType);

const getEnabledStatusFilter = <T>(
  label: string,
  name: Extract<keyof T, string>,
): FilterDefinition<T> => ({
  label,
  name,
  type: FilterDefinitionType.Select,
  options: filtersUtils.enabledStatusOptions,
  getDisplayName: (status: string) =>
    i18next.t(`filters.${status}`, { defaultValue: status }),
});

const enabledStatusOptions: FormikSelectOption[] = map(
  EnabledStatus,
  (status) => ({
    label: i18next.t(`filters.${status}`),
    value: status,
  }),
);

const getBooleanStatusFilter = <T>(
  label: string,
  name: Extract<keyof T, string>,
): FilterDefinition<T> => ({
  label,
  name,
  type: FilterDefinitionType.Select,
  options: [
    { value: 'true', label: formatUtils.formatBoolean(true) },
    { value: 'false', label: formatUtils.formatBoolean(false) },
  ],
  getDisplayName: (value: string) =>
    formatUtils.formatBoolean(value === 'true'),
});

export const filtersUtils = {
  getEnabledStatusFilter,
  getBooleanStatusFilter,
  getDataRangeFilters,
  enabledStatusOptions,
  getCommonFilters,
  encode,
  decode,
};
